/**
 * User management Zod schemas — shared FE ↔ BE.
 *
 * Per DESIGN.md §5.2 (Users endpoints) + REQUIREMENTS.md FR-USER-001..005.
 */
import { z } from 'zod';

/* ─── Constants ────────────────────────────────────────────────── */

/** Tenant-scoped roles. SYSTEM is reserved for users.id=1 (ADR-011) and NOT assignable. */
export const ROLE_CODES = ['OWNER', 'ADMIN', 'ESTIMATOR', 'PRODUCTION', 'SALES', 'VIEWER'] as const;
export type RoleCode = (typeof ROLE_CODES)[number];

/** Subset of roles that an inviter may grant. OWNER is not invitable; only one Owner per tenant. */
export const INVITABLE_ROLE_CODES = ['ADMIN', 'ESTIMATOR', 'PRODUCTION', 'SALES', 'VIEWER'] as const;
export type InvitableRoleCode = (typeof INVITABLE_ROLE_CODES)[number];

/* ─── List users (GET /users) ──────────────────────────────────── */

export const ListUsersQuerySchema = z.object({
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50).optional(),
  /** Filter: include deactivated users? Owner/Admin only. */
  includeDeactivated: z.coerce.boolean().optional(),
});

export type ListUsersQuery = z.infer<typeof ListUsersQuerySchema>;

/* ─── Invite user (POST /users/invite) ─────────────────────────── */

export const InviteUserSchema = z.object({
  email: z.string().trim().toLowerCase().email('Invalid email format').max(320),
  roleCode: z.enum(INVITABLE_ROLE_CODES, {
    errorMap: () => ({ message: 'Role must be one of ADMIN, ESTIMATOR, PRODUCTION, SALES, VIEWER' }),
  }),
});

export type InviteUserInput = z.infer<typeof InviteUserSchema>;

/* ─── Accept invitation (POST /users/invitations/:token/accept) ── */

export const AcceptInvitationSchema = z.object({
  password: z
    .string()
    .min(8, 'Password must be at least 8 characters')
    .max(128, 'Password too long')
    .regex(/[A-Za-z]/, 'Password must contain at least one letter')
    .regex(/\d/, 'Password must contain at least one digit'),
  firstName: z.string().trim().min(1, 'First name is required').max(100),
  lastName: z.string().trim().min(1, 'Last name is required').max(100),
});

export type AcceptInvitationInput = z.infer<typeof AcceptInvitationSchema>;

/* ─── Update user (PATCH /users/:id) ───────────────────────────── */

/**
 * A user may update their OWN profile (firstName/lastName). Only Owner/Admin
 * may change roleCode and may target other users.
 */
export const UpdateUserSchema = z
  .object({
    firstName: z.string().trim().min(1).max(100).optional(),
    lastName: z.string().trim().min(1).max(100).optional(),
    /** Only Owner/Admin may set this; service rejects with FORBIDDEN otherwise. */
    roleCode: z.enum(INVITABLE_ROLE_CODES).optional(),
  })
  .refine((data) => Object.keys(data).length > 0, {
    message: 'At least one field must be provided',
  });

export type UpdateUserInput = z.infer<typeof UpdateUserSchema>;

/* ─── User response DTOs ───────────────────────────────────────── */

export const UserDtoSchema = z.object({
  id: z.number().int().positive(),
  email: z.string(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  roleCode: z.string(),
  emailVerified: z.boolean(),
  isDeactivated: z.boolean(),
  /** ISO datetime of last successful login, or null if never logged in. */
  lastLoginAt: z.string().datetime().nullable(),
  createdDate: z.string().datetime(),
});

export type UserDto = z.infer<typeof UserDtoSchema>;

export const InvitationDtoSchema = z.object({
  id: z.number().int().positive(),
  email: z.string(),
  roleCode: z.string(),
  expiresAt: z.string().datetime(),
  /**
   * Plaintext token returned ONLY in the create-invite response (dev mode);
   * NEVER persisted, never returned again. In production an email is dispatched.
   */
  acceptToken: z.string().optional(),
});

export type InvitationDto = z.infer<typeof InvitationDtoSchema>;
